import React from 'react';

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const getBookingType = (items) => {
  if (!items || items.length === 0) return 'N/A';
  const title = items[0]?.title || '';
  if (title.includes('Round')) return 'Round Trip';
  if (title.includes('One Way')) return 'One Way';
  if (title.includes('Outstation')) return 'Outstation';
  if (title.includes('Local')) return 'Local';
  return title;
};

const formatHoursAsDays = (value) => {
  const hours = Number(value);
  if (!Number.isFinite(hours) || hours <= 0) return 'NA';

  if (hours < 24) {
    return `${hours} HR`;
  }

  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  const dayText = `${days} Day${days > 1 ? 's' : ''}`;
  const hourText = remainingHours ? ` ${remainingHours} HR` : '';

  return `${dayText}${hourText}`;
};

const getDurationRow = (value) => {
  const hours = Number(value);
  if (!Number.isFinite(hours) || hours <= 0) return ['No. of Hours', 'NA'];
  return hours > 24
    ? ['No of Days', formatHoursAsDays(hours)]
    : ['No. of Hours', `${hours} Hour${hours === 1 ? '' : 's'}`];
};

const getBookingDetailRows = (order, { hideOtp = false } = {}) => {
  if (!order) return [];
  const item = order.items?.[0] || {};
  const customer = order.details?.[0] || {};
  const driver = order.agentId || {};
  const pickupDateTime = [item.date, item.time].filter(Boolean).join(' ') || 'N/A';
  const bookingOtp = order.OTP ?? order.otp ?? order.orderOtp ?? order.bookingOtp ?? 'N/A';
  const alternativePhone = order.alternativePhone || item.alternativePhone || 'N/A';
  const pickupFullAddress =
    order.pickupFullAddress ||
    item.pickupFullAddress ||
    item.pickup ||
    order.location ||
    customer.address ||
    'N/A';

  const rows = [
    ['Order Id', `Order - ${order.orderId || order._id || 'N/A'}`],
    ['Customer Name', customer.username || 'N/A'],
    ['Pickup Time', pickupDateTime],
    ['Phone No', customer.phone || 'N/A'],
    ['Alternative Phone No', alternativePhone],
    ['Booking Date/Time', formatDateTime(order.createdAt)],
    ['Booking Sub', getBookingType(order.items)],
    ['Booking Type', item.bookingType || item.tripType || order.bookingType || 'Local'],
    ['Pickup Address', item.pickup || order.location || customer.address || 'N/A'],
    ['Pickup Full Address', pickupFullAddress],
    ['Pickup Location', item.pickup || order.location || 'N/A'],
    ['Destination Address', item.dropoff || 'N/A'],
    ['Destination Location', item.dropoff || 'N/A'],
    getDurationRow(item.hour),
    ['Booking Distance', item.distance || order.distance || 'NA'],
    ['Car Name', item.carType || item.title || 'NA'],
    ['Remarks', order.comment || item.remark || 'NA'],
    ['Driver Name', driver.username || 'Not Assigned'],
    ['Driver Phone', driver.phone || 'N/A'],
    ['Trip Start', formatDateTime(order.startTime)],
    ['Trip End', formatDateTime(order.endTime)],
    ['Total Amount', `₹${order.totalAmount ?? 0}`],
    ['Payment Mode', order.mode || 'N/A'],
  ];

  if (!hideOtp) {
    rows.push(['OTP', bookingOtp]);
  }

  return rows;
};

export const BookingDetailsButton = ({ order, onClick }) => (
  <button
    className="booking-details-btn"
    type="button"
    data-bs-toggle="modal"
    data-bs-target="#modal-booking-details"
    onClick={() => onClick(order)}
  >
    <i className="bi bi-card-text me-1"></i>Booking Details
  </button>
);

const BookingDetailsModal = ({ order, hideOtp = false }) => (
  <>
    <div className="modal fade" id="modal-booking-details" tabIndex={-1} aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content booking-details-modal-content border-0 shadow-lg">
          <div className="modal-header booking-details-modal-header border-0">
            <h5 className="modal-title">Booking</h5>
            <button type="button" className="btn-close btn-close-white" data-bs-dismiss="modal"></button>
          </div>
          <div className="modal-body booking-details-modal-body">
            <h2 className="booking-details-modal-title">Booking Details</h2>
            <div className="booking-details-grid">
              {getBookingDetailRows(order, { hideOtp }).map(([label, value], index) => (
                <div className="booking-details-item" key={`${label}-${index}`}>
                  <div className="booking-details-label">{label}</div>
                  <div className="booking-details-value">{value || 'N/A'}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="modal-footer border-0">
            <button type="button" className="btn btn-primary px-4 fw-bold" data-bs-dismiss="modal">Close</button>
          </div>
        </div>
      </div>
    </div>
    <style>{`
      .booking-details-btn {
        width: 100%;
        margin-top: 8px;
        border: 1px solid #dbeafe;
        background: #eff6ff;
        color: #1d4ed8;
        border-radius: 8px;
        padding: 7px 10px;
        font-size: 12px;
        font-weight: 700;
        cursor: pointer;
        transition: all .15s;
      }
      .booking-details-btn:hover { background: #1d4ed8; color: #fff; border-color: #1d4ed8; }
      .booking-details-modal-content { border-radius: 0; overflow: hidden; background: #fff; }
      .booking-details-modal-header {
        background: linear-gradient(90deg, #42c6ed, #0879bd);
        color: #fff;
        min-height: 72px;
        padding: 20px 26px;
      }
      .booking-details-modal-header .modal-title { font-size: 27px; font-weight: 500; letter-spacing: 0; }
      .booking-details-modal-body { padding: 36px 42px 28px; max-height: 72vh; overflow-y: auto; }
      .booking-details-modal-title {
        margin: 0 0 26px;
        font-size: 34px;
        line-height: 1.15;
        font-weight: 500;
        color: #263238;
        letter-spacing: 0;
      }
      .booking-details-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        column-gap: 38px;
      }
      .booking-details-item { min-height: 82px; padding: 0 0 18px; border-bottom: 1px solid #e6eaee; }
      .booking-details-label { margin-bottom: 14px; color: #202733; font-size: 20px; line-height: 1.2; font-weight: 800; }
      .booking-details-value {
        color: #202733;
        font-size: 18px;
        line-height: 1.45;
        font-weight: 500;
        white-space: pre-wrap;
        word-break: break-word;
      }
      @media (max-width: 640px) {
        .booking-details-modal-body { padding: 26px 22px; }
        .booking-details-modal-title { font-size: 28px; }
        .booking-details-grid { grid-template-columns: 1fr; }
        .booking-details-label { font-size: 17px; }
        .booking-details-value { font-size: 15px; }
      }
    `}</style>
  </>
);

export default BookingDetailsModal;
